import { GameProduct, VariantImage } from './types';

export const colorNameToHex: Record<string, string> = {
  burgandy: '#800020',
  red: '#FF0000',
  white: '#FFFFFF',
  blue: '#0000FF',
  black: '#000000',
  gray: '#808080',
  light_brown: '#D2B48C',
  beige: '#F5F5DC',
  yellow: '#FFFF00',
};

export const getDisplayColor = (colorValue: string): string => {
  return colorNameToHex[colorValue.toLowerCase()] || colorValue;
};

export const normalizeProductId = (id: string) => {
  return id.includes('/') ? id : `gid://shopify/Product/${id}`;
};

// Helper function to extract numeric product ID from GID format for API calls
export const extractNumericProductId = (id: string): string => {
  // If it's already numeric, return as is
  if (!id.includes('/')) {
    return id;
  }

  // Extract numeric ID from GID format: "gid://shopify/Product/8601270747467" -> "8601270747467"
  const parts = id.split('/');
  return parts[parts.length - 1];
};

// Helper function to find variant image by handle
export const getVariantImageByHandle = (handle: string, variantImages: VariantImage[]): string | null => {
  const variantImage = variantImages.find((item: VariantImage) => item.product.handle === handle);
  return variantImage ? variantImage.product.variantImage : null;
};

// Helper function to find variant image by product ID
export const getVariantImageById = (productId: string, variantImages: VariantImage[]): string | null => {
  const variantImage = variantImages.find((item: VariantImage) => item.product.id === productId);
  return variantImage ? variantImage.product.variantImage : null;
};

// Helper function to get color name from handle
export const getColorFromHandle = (handle: string): string => {
  // Extract color from handle (e.g., "black-tri-top" -> "black")
  const parts = handle.split('-');
  return parts[0] || 'Unknown';
};

// Helper function to check if a product has any available variants
export const hasAvailableVariants = (product: GameProduct): boolean => {
  return product.variants.edges.some((edge) => edge.node.availableForSale && edge.node.inventoryQuantity > 0);
};

// Helper function to convert rich text field JSON to HTML
export const convertRichTextToHTML = (richTextJson: string): string => {
  try {
    const richTextData = JSON.parse(richTextJson);

    const processNode = (node: any): string => {
      switch (node.type) {
        case 'root':
          return node.children?.map(processNode).join('') || '';

        case 'paragraph':
          const paragraphContent = node.children?.map(processNode).join('') || '';
          return `<p>${paragraphContent}</p>`;

        case 'heading':
          const headingLevel = node.level || 1;
          const headingContent = node.children?.map(processNode).join('') || '';
          return `<h${headingLevel}>${headingContent}</h${headingLevel}>`;

        case 'list':
          const listContent = node.children?.map(processNode).join('') || '';
          const listTag = node.listType === 'ordered' ? 'ol' : 'ul';
          return `<${listTag}>${listContent}</${listTag}>`;

        case 'list-item':
          const listItemContent = node.children?.map(processNode).join('') || '';
          return `<li>${listItemContent}</li>`;

        case 'text':
          let text = node.value || '';
          // Replace \n with <br> for line breaks
          text = text.replace(/\n/g, '<br>');

          // Apply formatting
          if (node.bold) {
            text = `<strong>${text}</strong>`;
          }
          if (node.italic) {
            text = `<em>${text}</em>`;
          }
          if (node.underline) {
            text = `<u>${text}</u>`;
          }

          return text;

        default:
          // For unknown node types, try to process children if they exist
          return node.children?.map(processNode).join('') || '';
      }
    };

    return processNode(richTextData);
  } catch (error) {
    console.error('Error parsing rich text field:', error);
    return richTextJson; // Return original text if parsing fails
  }
};
